import { SegmentedControl } from '@teach/ui'
import { GradientPicker } from '@/features/instructions/components/GradientPicker'
import { CoverBanner, CoverSquare } from './cover/CoverPreviews'
import { TitleColorPicker } from './cover/TitleColorPicker'
import { useCoverUpload } from './cover/useCoverUpload'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  title: string
  coverType: 'type1' | 'type2'
  onCoverTypeChange: (t: 'type1' | 'type2') => void
  gradient: string | null
  onGradientChange: (g: string | null) => void
  cardImage: string | null
  onCardImageChange: (url: string | null) => void
  titleColor: string | null
  onTitleColorChange: (c: string | null) => void
  authorName?: string | null
  authorAvatar?: string | null
  viewsCount?: number
  /** Статья на проверке: выбор обложки, градиента, цвета и загрузка погашены. */
  disabled: boolean
}

export const CoverTab = ({
  title,
  coverType,
  onCoverTypeChange,
  gradient,
  onGradientChange,
  cardImage,
  onCardImageChange,
  titleColor,
  onTitleColorChange,
  authorName,
  authorAvatar,
  viewsCount,
  disabled,
}: Props) => {
  const { fileRef, uploading, progress, openPicker, handleFile } = useCoverUpload({
    currentImage: cardImage,
    onChange: onCardImageChange,
  })
  const onUploadClick = disabled ? undefined : openPicker
  const m = useMessages(messages)

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-lg font-medium leading-none">{m.coverType}</h3>
        <SegmentedControl
          size="sm"
          options={[
            { id: 'type1', label: m.type1 },
            { id: 'type2', label: m.type2 },
          ]}
          value={coverType}
          onChange={onCoverTypeChange}
          disabled={disabled}
        />
      </div>

      <div className="space-y-3">
        <h3 className="text-lg font-medium leading-none">{m.header}</h3>
        <CoverBanner
          title={title}
          coverType={coverType}
          gradient={gradient}
          cardImage={cardImage}
          titleColor={titleColor}
          onUploadClick={onUploadClick}
        />
      </div>

      <div className="flex flex-col items-start gap-8 md:flex-row">
        <div className="w-full shrink-0 space-y-3 md:w-80">
          <h3 className="text-lg font-medium leading-none">{m.card}</h3>
          <CoverSquare
            title={title}
            coverType={coverType}
            gradient={gradient}
            cardImage={cardImage}
            titleColor={titleColor}
            authorName={authorName}
            authorAvatar={authorAvatar}
            viewsCount={viewsCount}
            onUploadClick={onUploadClick}
            progress={progress}
          />
        </div>
        <div className="w-full min-w-0 max-w-xl flex-1 space-y-8">
          <div className="space-y-3">
            <h3 className="text-lg font-medium leading-none">{m.gradient}</h3>
            <GradientPicker value={gradient} onChange={onGradientChange} disabled={disabled} />
          </div>
          <TitleColorPicker value={titleColor} onChange={onTitleColorChange} disabled={disabled} />
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  )
}
